import { createContext, useContext, useEffect, useState, useCallback } from "react";
import apiClient from "@/lib/apiClient";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem("user");
    return stored ? JSON.parse(stored) : null;
  });
  const [loading, setLoading] = useState(true);

  const persistUser = useCallback((u) => {
    setUser(u);
    if (u) localStorage.setItem("user", JSON.stringify(u));
    else localStorage.removeItem("user");
  }, []);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return null;
    }
    try {
      const { data } = await apiClient.get("/auth/me");
      persistUser(data);
      return data;
    } catch (e) {
      return null;
    } finally {
      setLoading(false);
    }
  }, [persistUser]);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  // Refresh session when the tab/iframe regains focus (e.g. returning from Stripe payment tab).
  useEffect(() => {
    const onFocus = () => {
      if (localStorage.getItem("token")) refreshUser();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refreshUser]);

  const login = async (email, password) => {
    const { data } = await apiClient.post("/auth/login", { email, password });
    localStorage.setItem("token", data.access_token);
    persistUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    // Web signups use magic-link verification; the caller does NOT auto-login.
    await apiClient.post("/auth/register", { ...payload, verification_mode: "magic" });
  };

  const logout = () => {
    localStorage.removeItem("token");
    persistUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser, setUser: persistUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
